import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { ThemeName, getTheme as getThemeFromThemes, defaultTheme, Theme } from '../themes/themes';

interface ThemeContextType {
  currentTheme: ThemeName;
  theme: Theme;
  setTheme: (themeName: ThemeName) => void;
  availableThemes: ThemeName[];
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  // Load theme from localStorage or use default
  const [currentTheme, setCurrentThemeState] = useState<ThemeName>(() => {
    const savedTheme = localStorage.getItem('app-theme') as ThemeName;
    const availableThemes: ThemeName[] = ['pure-white', 'sky-light', 'cloud-white', 'ocean-breeze', 'sunset-glow', 'arctic-fresh', 'neutral-elegance'];
    return savedTheme && availableThemes.includes(savedTheme)
      ? savedTheme 
      : defaultTheme;
  });

  const theme = getThemeFromThemes(currentTheme);
  const availableThemes: ThemeName[] = ['pure-white', 'sky-light', 'cloud-white', 'ocean-breeze', 'sunset-glow', 'arctic-fresh', 'neutral-elegance'];

  // Update CSS variables when theme changes
  useEffect(() => {
    const root = document.documentElement;
    const colors = theme.colors;

    // Set CSS custom properties
    root.style.setProperty('--theme-bg', colors.background);
    root.style.setProperty('--theme-surface', colors.surface);
    root.style.setProperty('--theme-card', colors.card);
    root.style.setProperty('--theme-section-bg', colors.sectionBackground);
    root.style.setProperty('--theme-border', colors.border);
    root.style.setProperty('--theme-input', colors.input);
    root.style.setProperty('--theme-text', colors.text);
    root.style.setProperty('--theme-text-secondary', colors.textSecondary);
    root.style.setProperty('--theme-text-light', colors.textLight);

    // Update body background
    document.body.style.backgroundColor = colors.background;
  }, [theme]);

  const setTheme = (themeName: ThemeName) => {
    setCurrentThemeState(themeName);
    localStorage.setItem('app-theme', themeName);
  };

  return (
    <ThemeContext.Provider value={{ currentTheme, theme, setTheme, availableThemes }}>
      {children}
    </ThemeContext.Provider>
  );
};

